// useChrome (Phase X step 3, B3) — the declarative page API over the chrome
// slots: a page states what hangs on the ceiling this commit, chrome renders
// it in the persistent frames.
//
//   useChrome({
//     bar:      { row, children, barRef, rowRef, onRowCommit, pieces? } | null,
//               //   pieces: true = the frame paints NO glass shell; the row's
//               //   published children wear TopBar's exported BarGlass each
//               //   (the lens bar's three-piece layout). Default false.
//     timeline: { node, gap } | null,
//     dock:     BottomDock content bag | null,
//   });
//
// EVERY slot is published EVERY commit, nulls included — that is load-bearing
// under the wave-B frame-hold contract: frames render the LAST content until
// the next publish lands (a per-page publisher cannot hold anything through
// its own unmount), and an explicit null publish is what CLEARS a frame a
// previous route filled. A page that omits `timeline` is saying "no timeline
// band on this route", not "whatever was there".
//
// Publish-per-commit (content closes over fresh page state); retract on true
// unmount HOLDS the frame (slots.jsx) until the arriving page's first
// publish. Monotonic ids: the arriving page always outranks the leaving one.
//
// `rightDefault` from the design sketch is deliberately NOT implemented yet:
// no converted page passes one, and a dead parameter would be a lie. It
// lands with its first consumer.
import { useLayoutEffect, useRef } from "react";

import { claimPublisherId, useSlots } from "./slots";

const useChrome = ({ bar = null, timeline = null, dock = null } = {}) => {
  const { publish, retract } = useSlots();
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = claimPublisherId();

  useLayoutEffect(() => {
    const id = idRef.current;
    publish("bar", id, bar);
    publish("timeline", id, timeline);
    publish("dock", id, dock);
  });

  useLayoutEffect(() => {
    const id = idRef.current;
    return () => {
      retract("bar", id);
      retract("timeline", id);
      retract("dock", id);
    };
  }, [retract]);
};

export default useChrome;

// Publishes null to every slot — a "this route shows no chrome" statement
// with a publisher id of its own. Rendered by a route ErrorBoundary's
// fallback (Main.jsx): a scene crash unmounts the route's only publisher,
// and under the hold contract the frames would otherwise keep the crashed
// page's bar/bands alive-and-interactive over the error card forever
// (review finding #3). Must render INSIDE SlotsProvider.
export const ClearChrome = () => {
  useChrome({});
  return null;
};
